import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

// Package
import dayjs from "dayjs";
import { isEmpty, size, cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";

// Util
import { messageKeyPrefix } from "../../../../utils/message.util";
import licenseUtil from "utils/license.util";
import { userPriv } from "utils/privilege.util";
import { getLocation } from "utils/googleMap.util";

// Slice
import { uiActions } from "stores/slice/ui";
import {
  fetchDeviceOverview,
  fetchDevicelist,
  searchOrg,
  addDevice,
  unregisterDevice,
  removeDevice,
  bulkAssign,
  bulkAssignToExistedSite,
  deviceSettingActions,
  bulkAssignToNewOrg,
  bulkAssignToNewSite,
} from "../../../../stores/slice/deviceSetting";
import { selectOrgLevel } from "../../../../stores/slice/pageContext";

// Const
import Nuclias from "const/nuclias";
import { userPrivilege } from "const/nuclias/user";
import {
  columnsORG,
  columnsMSP,
  defaultMessages,
  orgListLimit,
  defaultModalStatus,
  defaultDevicelisting,
} from "./const";
import { regex } from "const/nuclias/regex";

// Controller
import controller from "./controller";

// UI components
import Button from "../../../../components/Button";
import MessageBox from "../../../../components/MessageBox";
import InlineTitle from "../../../../components/InlineTitle";
import ModalContainer from "../../../../components/ModalContainer";
import PaginationContainer from "../../../../components/PaginationContainer";

// Modal
import AddDeviceModal from "./modals/AddDeviceModal";
import DeleteDeviceModal from "./modals/DeleteDeviceModal";
import AssignToModal from "./modals/AssignToModal";
import AssignLicenseModal from "./modals/AssignLicenseModal";
import ResultModal from "./modals/ResultModal";

// Sub compoment
import DevicesOverviewContainer from "./components/DevicesOverviewContainer.js";
import DeviceListTable from "./components/DeviceListTable";
import DevicesToolbarBox from "./components/DevicesToolbarBox";
import LeftButtonGroup from "./components/LeftButtonGroup";

// Main compoment
const Devices = () => {
  const { t } = useTranslation();
  const pageContext = useSelector((state) => state.pageContext);
  const isOrgLevel =
    useSelector(selectOrgLevel) === Nuclias.organizationType.org ? true : false;
  const managedOrg = isOrgLevel ? pageContext.selectedOrg : pageContext.si;
  const deviceOverview = useSelector(
    (state) => state.deviceSetting.deviceOverview
  );
  const deviceList = useSelector((state) => state.deviceSetting.deviceList);
  const mspPages = useSelector((state) => state.deviceSetting.mspPages);
  // const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();

  const [messages, setMessages] = useState({ ...defaultMessages });
  const [isOpenGenerateModal, setOpenGenerateModal] = useState(false);
  const [isFirstClickedYesBtn, setIsFirstClickedYesBtn] = useState(true);

  const [modalStatus, setModalStatus] = useState({ ...defaultModalStatus });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(Nuclias.tableSetting.pageLimit);

  const toolbarBoxRef = useRef();
  const tableRef = useRef();

  const [deleteBtnDisable, setDeleteBtnDisable] = useState(true);
  const [assignToBtnDisable, setAssignToBtnDisable] = useState(true);
  const [assignLicenseBtnDisable, setAssignLicenseBtnDisable] = useState(true);

  const [resultModalStatus, setResultModalStatus] = useState(false);
  const [resultModalList, setResultModalList] = useState([]);

  const [sortOrder, setSortOrder] = useState(1);
  const [sortField, setSortField] = useState("");
  const columns = isOrgLevel ? columnsORG : columnsMSP;
  const [filterData, setFilterData] = useState({});

  const [parentCheckedIDs, setParentCheckedIDs] = useState([]);

  const [seachOrgskip, setSeachOrgskip] = useState(0);

  const isManagerPrivilege = !userPriv(userPrivilege.manager);

  const initFetch = useCallback(async () => {
    let selectLimit = Nuclias.defaultPageLimit;
    try {
      dispatch(uiActions.showSpinner());
      dispatch(deviceSettingActions.cleanDeviceOverview());
      const deviceOverviewInfo = await dispatch(
        fetchDeviceOverview(managedOrg.id)
      ).unwrap();
      if (isOrgLevel) selectLimit = deviceOverviewInfo.deviceCount.total;
      await dispatch(fetchDevicelist([managedOrg.id, selectLimit])).unwrap();
    } catch (error) {
      setMessages({
        ...messages,
        danger: error.code,
      });
    } finally {
      dispatch(uiActions.hideSpinner());
    }
  }, [isOrgLevel, managedOrg.id, messages, dispatch]);

  // device list init
  useEffect(() => {
    initFetch();
  }, [initFetch]);

  const changeModalStatus = useCallback(
    (type, status) => {
      const tmpModalStatus = { ...modalStatus };
      tmpModalStatus[type].status = status;
      setModalStatus(tmpModalStatus);
    },
    [modalStatus]
  );

  const deleteFromOrg = async () => {
    const convertArrayToObject = parentCheckedIDs.map((val) => val);
    const removeData = {
      ids: convertArrayToObject,
    };
    let selectLimit = pageLimit;
    setCurrentPage(1);
    setParentCheckedIDs([]);
    setDeleteBtnDisable(true);
    try {
      changeModalStatus("deleteDevice", false);
      dispatch(uiActions.showSpinner());
      await dispatch(removeDevice(removeData)).unwrap();
      const deviceOverviewInfo = await dispatch(
        fetchDeviceOverview(managedOrg.id)
      ).unwrap();
      if (isOrgLevel) selectLimit = deviceOverviewInfo.deviceCount.total;
      deviceListFetch(
        selectLimit,
        defaultDevicelisting.skipRows,
        defaultDevicelisting.sortField,
        defaultDevicelisting.sortOrder,
        isOrgLevel,
        filterData
      );
    } catch (error) {
      setMessages({
        ...messages,
        danger: error.code,
      });
    } finally {
      tableRef.current.resetCheckBox();
      dispatch(uiActions.hideSpinner());
    }
  };

  const moveToInventory = async () => {
    const convertArrayToObject = parentCheckedIDs.map((val) => val);
    const removeData = {
      ids: convertArrayToObject,
    };
    let selectLimit = pageLimit;
    setCurrentPage(1);
    setParentCheckedIDs([]);
    setDeleteBtnDisable(true);
    try {
      changeModalStatus("deleteDevice", false);
      dispatch(uiActions.showSpinner());
      await dispatch(unregisterDevice(removeData)).unwrap();
      const deviceOverviewInfo = await dispatch(
        fetchDeviceOverview(managedOrg.id)
      ).unwrap();
      if (isOrgLevel) selectLimit = deviceOverviewInfo.deviceCount.total;
      deviceListFetch(
        selectLimit,
        defaultDevicelisting.skipRows,
        defaultDevicelisting.sortField,
        defaultDevicelisting.sortOrder,
        isOrgLevel,
        filterData
      );
    } catch (error) {
      setMessages({
        ...messages,
        danger: error.code,
      });
    } finally {
      tableRef.current.resetCheckBox();
      dispatch(uiActions.hideSpinner());
    }
  };

  const addDevicebyInput = async (deviceUIDs) => {
    const convertArrayToObject = deviceUIDs.map((val) => ({ uid: val }));
    const addData = {
      organizationId: managedOrg.id,
      devices: convertArrayToObject,
    };
    let selectLimit = pageLimit;
    setCurrentPage(1);
    try {
      changeModalStatus("addDevice", false);
      dispatch(uiActions.showSpinner());
      const result = await dispatch(addDevice(addData)).unwrap();
      if (size(result) > 0) {
        setResultModalList(result);
        setResultModalStatus(true);
      }

      const deviceOverviewInfo = await dispatch(
        fetchDeviceOverview(managedOrg.id)
      ).unwrap();
      if (isOrgLevel) selectLimit = deviceOverviewInfo.deviceCount.total;
      deviceListFetch(
        selectLimit,
        defaultDevicelisting.skipRows,
        defaultDevicelisting.sortField,
        defaultDevicelisting.sortOrder,
        isOrgLevel,
        filterData
      );
    } catch (error) {
      setMessages({
        ...messages,
        danger: error.code,
      });
    } finally {
      dispatch(uiActions.hideSpinner());
    }
  };

  const assignLicenseToExisting = async (dataObj) => {
    // id to uid
    const checkedUIDs = formatDeviceListTargetData
      .filter((device) => {
        if (parentCheckedIDs.includes(device.id)) return true;
        return false;
      })
      .map((device) => {
        return device.uid;
      });
    const assignLicenseToData =
      dataObj.selectedDeviceStatus.value === "Unused"
        ? {
            orgId: dataObj.selectedOrgId,
            storeId: dataObj.selectedSite.id,
            deviceGroupId: dataObj.selectedProfile.id,
            deviceUids: checkedUIDs,
            unit: dataObj.selectedLicenseTerms.unit,
            period: dataObj.selectedLicenseTerms.period,
          }
        : {
            orgId: dataObj.selectedOrgId,
            storeId: dataObj.selectedSite.id,
            deviceGroupId: dataObj.selectedProfile.id,
            deviceUids: checkedUIDs,
            licenseKey: dataObj.selectedLicense,
          };
    let selectLimit = pageLimit;
    setCurrentPage(1);
    setParentCheckedIDs([]);
    setDeleteBtnDisable(true);
    try {
      changeModalStatus("assignTo", false);
      dispatch(uiActions.showSpinner());
      const result = !isOrgLevel
        ? await dispatch(
            bulkAssign([assignLicenseToData, managedOrg.id])
          ).unwrap()
        : await dispatch(
            bulkAssignToExistedSite([assignLicenseToData, managedOrg.id])
          ).unwrap();
      if (size(result) > 0) {
        setResultModalList(result.log);
        setResultModalStatus(true);
      }

      const deviceOverviewInfo = await dispatch(
        fetchDeviceOverview(managedOrg.id)
      ).unwrap();
      if (isOrgLevel) selectLimit = deviceOverviewInfo.deviceCount.total;
      deviceListFetch(
        selectLimit,
        defaultDevicelisting.skipRows,
        defaultDevicelisting.sortField,
        defaultDevicelisting.sortOrder,
        isOrgLevel,
        filterData
      );
    } catch (error) {
      submitModal([{ error: error, uid: parentCheckedIDs.toString() }]);
    } finally {
      tableRef.current.resetCheckBox();
      dispatch(uiActions.hideSpinner());
    }
  };

  const assignLicenseToNew = async (dataObj) => {
    // id to uid
    const checkedUIDs = formatDeviceListTargetData
      .filter((device) => {
        if (parentCheckedIDs.includes(device.id)) return true;
        return false;
      })
      .map((device) => {
        return device.uid;
      });

    const locationResult = await getLocation({
      address: dataObj.newAddress,
      country: dataObj.selectedCountry.country.title,
    });

    const assignLicenseToData =
      dataObj.selectedDeviceStatus.value === "Unused"
        ? {
            storeName: dataObj.newSite,
            address: {
              country: dataObj.selectedCountry.country.title,
              address: dataObj.newAddress,
              city: dataObj.selectedCountry.city.city,
              timezone: dataObj.selectedCountry.city.timezone,
              fullTimezone: dataObj.selectedCountry.city.title,
            },
            location: {
              latitude: locationResult.latitude,
              longitude: locationResult.longitude,
            },
            storeConfig: {
              dayLight: true,
              dayLightString: dataObj.selectedCountry.city.dsts,
            },
            deviceUids: checkedUIDs,
            unit: dataObj.selectedLicenseTerms.unit,
            period: dataObj.selectedLicenseTerms.period,
          }
        : {
            storeName: dataObj.newSite,
            address: {
              country: dataObj.selectedCountry.country.title,
              address: dataObj.newAddress,
              city: dataObj.selectedCountry.city.city,
              timezone: dataObj.selectedCountry.city.timezone,
              fullTimezone: dataObj.selectedCountry.city.title,
            },
            location: {
              latitude: 0,
              longitude: 0,
            },
            storeConfig: {
              dayLight: true,
              dayLightString: dataObj.selectedCountry.city.dsts,
            },
            deviceUids: checkedUIDs,
            licenseKey: dataObj.selectedLicense,
          };
    if (!isOrgLevel) {
      assignLicenseToData.orgName = dataObj.newOrganization;
    }
    let selectLimit = pageLimit;
    setCurrentPage(1);
    setParentCheckedIDs([]);
    setDeleteBtnDisable(true);
    try {
      changeModalStatus("assignTo", false);
      dispatch(uiActions.showSpinner());
      const result = !isOrgLevel
        ? await dispatch(
            bulkAssignToNewOrg([assignLicenseToData, managedOrg.id])
          ).unwrap()
        : await dispatch(
            bulkAssignToNewSite([assignLicenseToData, managedOrg.id])
          ).unwrap();
      if (size(result) > 0) {
        setResultModalList(result.log);
        setResultModalStatus(true);
      }
      const deviceOverviewInfo = await dispatch(
        fetchDeviceOverview(managedOrg.id)
      ).unwrap();
      if (isOrgLevel) selectLimit = deviceOverviewInfo.deviceCount.total;
      deviceListFetch(
        selectLimit,
        defaultDevicelisting.skipRows,
        defaultDevicelisting.sortField,
        defaultDevicelisting.sortOrder,
        isOrgLevel,
        filterData
      );
    } catch (error) {
      submitModal([{ error: error, uid: parentCheckedIDs.toString() }]);
    } finally {
      tableRef.current.resetCheckBox();
      dispatch(uiActions.hideSpinner());
    }
  };

  const orgListFetch = useCallback(
    async (orgName, skip) => {
      try {
        dispatch(uiActions.showSpinner());
        await dispatch(
          searchOrg([managedOrg.id, orgName, skip, orgListLimit])
        ).unwrap();
      } catch (error) {
        setMessages({
          ...messages,
          danger: error.code,
        });
      } finally {
        dispatch(uiActions.hideSpinner());
      }
    },
    [dispatch, seachOrgskip, managedOrg.id]
  );

  const deviceListFetch = useCallback(
    async (
      limit,
      skip,
      sort = defaultDevicelisting.sortField,
      reverse = defaultDevicelisting.sortOrder,
      isOrgLevel,
      filter = {}
    ) => {
      let mspSortField = defaultDevicelisting.sortField;
      let mspSortOrder = defaultDevicelisting.sortOrder;
      try {
        if (sort !== defaultDevicelisting.sortField) {
          // use parameter sort order
          mspSortField = sort;
          mspSortOrder = reverse;
        } else {
          // use state sort order
          mspSortField = sortField;
          mspSortOrder = sortOrder;
        }
        dispatch(uiActions.showSpinner());
        await dispatch(
          fetchDevicelist([
            managedOrg.id,
            limit,
            skip,
            mspSortField,
            mspSortOrder,
            isOrgLevel,
            filter,
          ])
        ).unwrap();
      } catch (error) {
        setMessages({
          ...messages,
          danger: error.code,
        });
      } finally {
        dispatch(uiActions.hideSpinner());
      }
      setSortOrder(mspSortOrder);
      setSortField(mspSortField);
    },
    [sortOrder, sortField, dispatch, managedOrg.id, messages]
  );

  const orgDeviceListFetch = useCallback(
    async (
      limit,
      skip,
      sort = defaultDevicelisting.sortField,
      reverse = defaultDevicelisting.sortOrder,
      isOrgLevel,
      filter = {},
      orgName,
      orgNameSkip
    ) => {
      let mspSortField = defaultDevicelisting.sortField;
      let mspSortOrder = defaultDevicelisting.sortOrder;
      try {
        dispatch(uiActions.showSpinner());
        if (sort !== defaultDevicelisting.sortOrder) {
          // use parameter sort order
          mspSortField = sort;
          mspSortOrder = reverse;
        } else {
          // use state sort order
          mspSortField = sortField;
          mspSortOrder = sortOrder;
        }
        await dispatch(
          fetchDevicelist([
            managedOrg.id,
            limit,
            skip,
            mspSortField,
            mspSortOrder,
            isOrgLevel,
            filter,
          ])
        ).unwrap();
        await dispatch(
          searchOrg([managedOrg.id, orgName, orgNameSkip, orgListLimit])
        ).unwrap();
      } catch (error) {
        setMessages({
          ...messages,
          danger: error.code,
        });
      } finally {
        dispatch(uiActions.hideSpinner());
      }
      setSortOrder(mspSortOrder);
      setSortField(mspSortField);
    },
    [sortOrder, sortField, dispatch, managedOrg.id, messages]
  );

  const handleClickYes = () => {
    if (isFirstClickedYesBtn) {
      setMessages({
        generateError:
          "The file is still being generating. You will receive an email once it's ready to download.",
      });
    } else {
      setOpenGenerateModal(false);
    }
  };

  // device info functions
  const getLicenseStatus = (licenseState) => {
    switch (licenseState) {
      case Nuclias.deviceConfig.licenseState.ACTIVATED:
        return "cb456215c3";
      case Nuclias.deviceConfig.licenseState.NOT_ACTIVATED:
        return "463050ae63";
      case Nuclias.deviceConfig.licenseState.EXHAUSTED:
        return "a1a698ed9b";
      case Nuclias.deviceConfig.licenseState.NO_LICENSE:
        return "d2e37a6894";
      case Nuclias.deviceConfig.licenseState.SUSPENDED:
        return "f47c23e163";
      default:
        return "";
    }
  };

  const getStatus = (deviceItem) => {
    const isItemRegistered =
      deviceItem.registrationState === Nuclias.deviceStates.registered;
    const isItemOnline =
      deviceItem.onlineStatus === Nuclias.deviceStates.online;
    const isItemOffline =
      deviceItem.lastOfflineTime &&
      deviceItem.lastOfflineTime > dayjs().subtract(7, "days").unix();
    let resultStatus = "";
    if (isItemRegistered && isItemOnline)
      resultStatus = Nuclias.statusList[1].statusType; // online
    else if (isItemOffline)
      resultStatus = Nuclias.statusList[2].statusType; // offline
    else resultStatus = Nuclias.statusList[3].statusType; // dormant (offline over 7 days)

    return resultStatus;
  };
  const getLicenseKeys = (licenses) => {
    const showKeyStr =
      licenses.length > 0
        ? licenses.length === 1
          ? licenses[0].key
          : licenses[0].key + "(" + licenses.length + ")"
        : "";

    return showKeyStr;
  };

  const isExpiredInDays = (days, expiredTimeStamp) => {
    const timeStampAfterDays = dayjs().add(days, "day").unix();
    return expiredTimeStamp > timeStampAfterDays ? false : true;
  };

  const setIsAssignedMSP = (eachDevice) => {
    const resultIsAssigned =
      !eachDevice.isInventory ||
      eachDevice.profileName !== "__inventory" ||
      eachDevice.siteName !== null
        ? true
        : false;
    return resultIsAssigned ? "used" : "not use"; // use "not use" instead of unused to avoid partial match
  };

  const setIsAssignedORG = (registrationState) => {
    const isInventoryRegister =
      registrationState !== Nuclias.deviceStates.unregistered;
    return isInventoryRegister ? "used" : "not use"; // use "not use" instead of unused to avoid partial match
  };

  const checkIsAssignedDevicesSelected = () => {
    return (
      formatDeviceListTargetData.filter((device) => {
        return parentCheckedIDs.includes(device.id) && device.used === "used";
      }).length > 0
    );
  };
  const checkIsNotAssignedDevicesSelected = () => {
    return (
      formatDeviceListTargetData.filter((device) => {
        return (
          parentCheckedIDs.includes(device.id) && device.used === "not use"
        );
      }).length > 0
    );
  };

  const checkIsDiffDevicesModelSelected = () => {
    const selectedDevices = formatDeviceListTargetData.filter((device) => {
      return parentCheckedIDs.includes(device.id);
    });
    const selectedModelList = selectedDevices.map((device) => {
      return device.modelName.split(regex.secondModelName)[0];
    });
    const uniqSelectedModelList = selectedModelList.filter(
      (modelName, index, arr) => {
        return arr.indexOf(modelName) === index;
      }
    );
    return uniqSelectedModelList.length > 1;
  };

  const isAssignToDisable = () => {
    let isDisable = false;
    if (!isManagerPrivilege) isDisable = true;
    if (parentCheckedIDs.length === 0) isDisable = true;
    if (checkIsAssignedDevicesSelected()) isDisable = true;
    if (checkIsDiffDevicesModelSelected()) isDisable = true;

    return isDisable;
  };

  const isAssignLicenseDisable = () => {
    let isDisable = false;
    if (!isManagerPrivilege) isDisable = true;
    if (parentCheckedIDs.length === 0) isDisable = true;
    if (checkIsNotAssignedDevicesSelected()) isDisable = true;

    return isDisable;
  };

  // generate device list
  const formatDeviceListTargetData = useMemo(() => {
    let deviceInfos = [];
    deviceList?.forEach((eachDevice) => {
      let tmpDeviceInfo = {
        id: null,
        onlineStatus: null,
        macAddress: "",
        uid: "",
        serialNumber: "",
        modelName: "",
        name: "",
        organization: null,
        organizationId: null,
        level: "",
        siteName: "",
        profileName: "",
        licenseKey: "",
        licenseState: "",
        activationDate: null,
        expirationDate: null,
        productCategory: "",
        used: false,
        expirationTimeStamp: null,
        isInventory: null,
      };
      tmpDeviceInfo.id = eachDevice.id;
      tmpDeviceInfo.onlineStatus = getStatus(eachDevice);
      tmpDeviceInfo.macAddress =
        eachDevice.macAddress == null ? "" : eachDevice.macAddress;
      tmpDeviceInfo.modelName = eachDevice.modelName;
      tmpDeviceInfo.serialNumber = eachDevice.serialNumber;
      tmpDeviceInfo.uid = eachDevice.uid;
      tmpDeviceInfo.name = eachDevice.name;
      tmpDeviceInfo.productCategory = eachDevice.deviceType.toLowerCase();
      if (!isOrgLevel) {
        tmpDeviceInfo.organization = eachDevice.orgName;
        tmpDeviceInfo.organizationId = eachDevice.orgInstanceId;
        tmpDeviceInfo.level = eachDevice.level;
        tmpDeviceInfo.licenseKey = getLicenseKeys(eachDevice.licenses);
      }
      tmpDeviceInfo.used = isOrgLevel
        ? setIsAssignedORG(eachDevice.registrationState)
        : setIsAssignedMSP(eachDevice);
      tmpDeviceInfo.siteName = eachDevice.siteName;
      tmpDeviceInfo.profileName =
        eachDevice.profileName === "__inventory" ? "" : eachDevice.profileName;
      tmpDeviceInfo.licenseState = getLicenseStatus(eachDevice.licenseState);
      const { firstStartDateFormat, expirationDateFormat, expirationUnix } =
        licenseUtil.getDeviceLicenseRemainDate(
          eachDevice.licenseState,
          eachDevice.licenses
        );
      tmpDeviceInfo.activationDate = firstStartDateFormat;
      tmpDeviceInfo.expirationDate = expirationDateFormat;
      tmpDeviceInfo.expirationTimeStamp = expirationUnix;
      tmpDeviceInfo.isInventory = eachDevice.isInventory;
      deviceInfos.push(tmpDeviceInfo);
    });
    return deviceInfos;
  }, [deviceList, isOrgLevel]);

  // search filter functions
  const appendFilterData = (item) => {
    setFilterData((filterData) => ({ ...filterData, ...item }));
    setCurrentPage(1);
    if (!isOrgLevel) {
      const removedFilterData = removeFilter(
        cloneDeep({ ...filterData, ...item })
      );
      deviceListFetch(
        pageLimit,
        defaultDevicelisting.skipRows,
        defaultDevicelisting.sortField,
        defaultDevicelisting.sortOrder,
        isOrgLevel,
        removedFilterData
      );
    }
  };

  const appendPureFilterData = (item) => {
    setFilterData((filterData) => ({ ...filterData, ...item }));
    setCurrentPage(1);
  };

  const setDeviceTypeFilter = useCallback((item) => {
    appendFilterData({
      productCategory: item.productCategory,
      onlineStatus: "",
    });
    toolbarBoxRef.current.setChildDeviceType({
      value: item.productCategory,
      title: item.title,
    });
  }, []);

  const removeFilter = (clonedFilterData) => {
    for (const key in clonedFilterData) {
      if (Nuclias.searchItemUseSelect.includes(key)) {
        if (
          clonedFilterData[key].toLowerCase() === "all" ||
          clonedFilterData[key].toLowerCase() === "both" ||
          clonedFilterData[key].toLowerCase() === ""
        ) {
          delete clonedFilterData[key];
          continue;
        }
      } else {
        if (clonedFilterData[key].toLowerCase() === "") {
          delete clonedFilterData[key];
          continue;
        }
      }
    }
    return clonedFilterData;
  };

  const searchFilter = useCallback(
    (formatedDevices) => {
      if (isEmpty(filterData)) return formatedDevices;
      const removedFilterData = removeFilter(cloneDeep(filterData));
      if (isEmpty(removedFilterData)) return formatedDevices;
      const filterResult = formatedDevices.filter((device) => {
        let match = true;
        for (const key in removedFilterData) {
          if (key === "expirationTimeStamp") {
            const filterField = removedFilterData[key];
            const deviceField = device[key];
            match =
              deviceField === 0
                ? false
                : isExpiredInDays(filterField, deviceField);
          } else {
            if (
              !device[key]
                .toLowerCase()
                .includes(removedFilterData[key].toString().toLowerCase())
            ) {
              match = false;
            }
          }
        }
        return match;
      });
      return filterResult;
    },
    [filterData]
  );

  // show result model if response data exist
  const submitModal = (result) => {
    if (size(result) > 0) {
      setResultModalList(result);
      setResultModalStatus(true);
    }
  };

  return (
    <div className="layout-container layout-container--column layout-container--fluid">
      <MessageBox
        show={!!messages.danger}
        label={t(`${messageKeyPrefix.e.s}${messages.danger}`)}
        variant="danger"
        onClose={() => setMessages({ ...messages, danger: null })}
      />
      <MessageBox
        show={!!messages.success}
        label={t(messages.success)}
        variant="success"
        onClose={() => setMessages({ ...defaultMessages, success: null })}
      />
      <MessageBox
        show={!!messages.error}
        label={t(`${messageKeyPrefix.e.s}${messages.error}`)}
        variant="danger"
        onClose={() => setMessages({ ...defaultMessages, error: null })}
      />
      <MessageBox
        show={!!messages.warning}
        label={t(messages.warning)}
        variant="warning"
        onClose={() => setMessages({ ...defaultMessages, warning: null })}
      />

      <InlineTitle label={t("8c57368224")} isNonUnderline />

      <DevicesOverviewContainer
        deviceOverview={deviceOverview}
        setDeviceTypeFilter={setDeviceTypeFilter}
      />

      <div className="d-flex justify-content-between">
        <LeftButtonGroup
          modalStatus={modalStatus}
          changeModalStatus={changeModalStatus}
          addBtnDisable={!isManagerPrivilege ? true : false}
          deleteBtnDisable={!isManagerPrivilege ? true : deleteBtnDisable}
          // assignToBtnDisable={assignToBtnDisable}
          assignToBtnDisable={!isManagerPrivilege ? true : isAssignToDisable()}
          assignLicenseBtnDisable={
            !isManagerPrivilege ? true : isAssignLicenseDisable()
          }
        />
        <DevicesToolbarBox
          changeMessages={setMessages}
          changeOpenGenerateModalStatus={setOpenGenerateModal}
          isOrgLevel={isOrgLevel}
          appendFilterData={appendFilterData}
          appendPureFilterData={appendPureFilterData}
          filterData={filterData}
          pageLimit={pageLimit}
          removeFilter={removeFilter}
          orgDeviceListFetch={orgDeviceListFetch}
          orgListLimit={orgListLimit}
          orgListFetch={orgListFetch}
          seachOrgskip={seachOrgskip}
          setSeachOrgskip={setSeachOrgskip}
          ref={toolbarBoxRef}
        />
      </div>
      {deviceList && (
        <DeviceListTable
          data={
            isOrgLevel
              ? searchFilter(formatDeviceListTargetData)
              : formatDeviceListTargetData
          }
          columns={columns}
          currentPage={currentPage}
          pageLimit={pageLimit}
          isOrgLevel={isOrgLevel}
          deviceListFetch={deviceListFetch}
          filterData={filterData}
          setDeleteBtnDisable={setDeleteBtnDisable}
          setParentCheckedIDs={setParentCheckedIDs}
          isManagerPrivilege={isManagerPrivilege}
          ref={tableRef}
        />
      )}
      <PaginationContainer
        previousLabel={"dd1f775e44"}
        nextLabel={"10ac3d0425"}
        isCurrentPageId={currentPage}
        total={
          deviceList
            ? isOrgLevel
              ? searchFilter(formatDeviceListTargetData).length
              : mspPages
            : 0
        }
        onPageChange={(currentPageNum) => {
          setCurrentPage(currentPageNum);
          if (!isOrgLevel) {
            deviceListFetch(
              pageLimit,
              (currentPageNum - 1) * pageLimit,
              defaultDevicelisting.sortField,
              defaultDevicelisting.sortOrder,
              isOrgLevel,
              filterData
            );
          }
        }}
        onEntryLimitChange={(currentPageLimit) => {
          setPageLimit(currentPageLimit);
          setCurrentPage(1);
          if (!isOrgLevel) {
            deviceListFetch(
              currentPageLimit,
              defaultDevicelisting.skipRows,
              defaultDevicelisting.sortField,
              defaultDevicelisting.sortOrder,
              isOrgLevel,
              filterData
            );
          }
        }}
      />

      {/* Generate modal */}
      <ModalContainer
        modalWidthType="modal-500px"
        openModal={isOpenGenerateModal}
        closeModal={() => setOpenGenerateModal(false)}
      >
        <div className="header">
          <div className="title">Generate inventory list</div>
        </div>
        <div className="body">
          <MessageBox
            show={!!messages.generateError}
            label={messages.generateError}
            variant="danger"
            dismissible
            onClose={() => {
              setIsFirstClickedYesBtn(false);
              setMessages({
                generateError: null,
              });
            }}
          />

          <div className="mb-1">
            Do you want to generate the inventory list?
          </div>
          <div className="mb-1">
            You will receive an email to inform you the inventory list is ready
            for download after few minutes.
          </div>
        </div>
        <div className="footer">
          <Button
            label="Cancel"
            className="btn-cancel"
            onClick={() => setOpenGenerateModal(false)}
          />
          <Button
            label="Yes"
            className="btn-submit"
            onClick={() => handleClickYes()}
          />
        </div>
      </ModalContainer>

      {/* Modals */}
      <AddDeviceModal
        modalStatus={modalStatus}
        changeModalStatus={changeModalStatus}
        addDevice={addDevicebyInput}
        submitModal={submitModal}
        orgId={managedOrg.id}
      />
      <DeleteDeviceModal
        modalStatus={modalStatus}
        changeModalStatus={changeModalStatus}
        moveToInventory={moveToInventory}
        deleteFromOrg={deleteFromOrg}
      />
      <AssignToModal
        modalStatus={modalStatus}
        changeModalStatus={changeModalStatus}
        submitModal={submitModal}
        isOrgLevel={isOrgLevel}
        filterData={filterData}
        pageLimit={pageLimit}
        orgDeviceListFetch={orgDeviceListFetch}
        orgListLimit={orgListLimit}
        orgListFetch={orgListFetch}
        seachOrgskip={seachOrgskip}
        setSeachOrgskip={setSeachOrgskip}
        formatDeviceListTargetData={formatDeviceListTargetData}
        parentCheckedIDs={parentCheckedIDs}
        orgId={managedOrg.id}
        assignLicenseToExisting={assignLicenseToExisting}
        assignLicenseToNew={assignLicenseToNew}
      />
      <AssignLicenseModal
        modalStatus={modalStatus}
        changeModalStatus={changeModalStatus}
        formatDeviceListTargetData={formatDeviceListTargetData}
        parentCheckedIDs={parentCheckedIDs}
        orgId={managedOrg.id}
      />
      {size(resultModalList) > 0 && (
        <ResultModal
          {...{
            results: resultModalList,
            resultModalStatus,
            setResultModalStatus,
          }}
        />
      )}
    </div>
  );
};

export default Devices;
